import React, { useRef, useEffect, useState, forwardRef } from 'react';
import { createPortal } from 'react-dom';

export interface PopoverProps {
  /**
   * Whether the popover is visible
   */
  isVisible: boolean;
  
  /**
   * Reference element to position the popover relative to
   */
  targetRef: React.RefObject<HTMLElement>;
  
  /**
   * Subtitle text to display above the main content
   */
  subtitle?: React.ReactNode | string;
  
  /**
   * Main content of the popover
   */
  content: React.ReactNode | string;
  
  /**
   * State text to display below the main content
   * Can be used for error messages, success messages, etc.
   */
  state?: React.ReactNode | string;
  
  /**
   * Type of state message
   * - 'default': Uses subtitle color
   * - 'error': Uses error color
   * - 'success': Uses success color
   * - 'warning': Uses warning color
   * - 'info': Uses info color
   */
  stateType?: 'default' | 'error' | 'success' | 'warning' | 'info';
  
  /**
   * Color variant for the popover
   * - 'default': Standard layer colors
   * - 'subtle': Softer layer colors
   * - 'strong': Stronger background colors
   * - 'glassmorphic': Blur backdrop with transparency
   */
  variant?: 'default' | 'subtle' | 'strong' | 'glassmorphic';
  
  /**
   * Position of the popover relative to the target
   * - 'above': Show above the target
   * - 'below': Show below the target (default)
   * - 'left': Show to the left
   * - 'right': Show to the right
   */
  position?: 'above' | 'below' | 'left' | 'right';
  
  /**
   * Whether to show the arrow pointing to the target
   */
  showArrow?: boolean;
  
  /**
   * Maximum width of the popover
   */
  maxWidth?: string | number;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Callback when mouse enters the popover
   */
  onMouseEnter?: () => void;
  
  /**
   * Callback when mouse leaves the popover
   */
  onMouseLeave?: () => void;
  
  /**
   * Custom styles
   */
  style?: React.CSSProperties;
}

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(({
  isVisible,
  targetRef,
  subtitle,
  content,
  state,
  stateType = 'default',
  variant = 'default',
  position = 'below',
  showArrow = true,
  maxWidth = '400px',
  className = '',
  onMouseEnter,
  onMouseLeave,
  style = {}
}, ref) => {
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const internalRef = useRef<HTMLDivElement>(null);
  const popoverRef = ref || internalRef;
  
  // Calculate popover position
  const updatePosition = () => {
    if (!targetRef.current || !isVisible) return;
    
    const targetRect = targetRef.current.getBoundingClientRect();
    const popoverEl = (popoverRef as React.RefObject<HTMLDivElement>).current;
    
    if (!popoverEl) {
      // If popover not yet rendered, try again after next paint
      requestAnimationFrame(() => {
        updatePosition();
      });
      return;
    }
    
    const popoverRect = popoverEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8; // Space between target and popover
    
    let top = 0;
    let left = 0;
    
    // Calculate position based on preference
    switch (position) {
      case 'above':
        top = targetRect.top - popoverRect.height - margin;
        left = targetRect.left + (targetRect.width / 2) - (popoverRect.width / 2);
        
        // Flip to below if no room above
        if (top < margin) {
          top = targetRect.bottom + margin;
        }
        break;
        
      case 'below':
        top = targetRect.bottom + margin;
        left = targetRect.left + (targetRect.width / 2) - (popoverRect.width / 2);
        
        // Flip to above if no room below
        if (top + popoverRect.height > viewportHeight - margin) {
          top = targetRect.top - popoverRect.height - margin;
        }
        break;
        
      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (popoverRect.height / 2);
        left = targetRect.left - popoverRect.width - margin;
        
        // Flip to right if no room on left
        if (left < margin) {
          left = targetRect.right + margin;
        }
        break;
        
      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (popoverRect.height / 2);
        left = targetRect.right + margin;
        
        // Flip to left if no room on right
        if (left + popoverRect.width > viewportWidth - margin) {
          left = targetRect.left - popoverRect.width - margin;
        }
        break;
    }
    
    // Ensure popover stays within viewport horizontally
    if (left + popoverRect.width > viewportWidth - margin) {
      left = viewportWidth - popoverRect.width - margin;
    }
    if (left < margin) {
      left = margin;
    }
    
    // Ensure popover stays within viewport vertically
    if (top + popoverRect.height > viewportHeight - margin) {
      top = viewportHeight - popoverRect.height - margin;
    }
    if (top < margin) {
      top = margin;
    }
    
    setPopoverPosition({ top, left });
    setIsPositioned(true);
  };
  
  // Update position when visibility changes or dependencies update
  useEffect(() => {
    if (isVisible) {
      setIsPositioned(false);
      updatePosition();
      
      // Add event listeners for scroll and resize
      const handleUpdate = () => updatePosition();
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isVisible, targetRef.current, subtitle, content, state]);
  
  // Don't render if not visible
  if (!isVisible) return null;
  
  const popoverClasses = [
    'core-popover',
    `core-popover--${variant}`,
    `core-popover--${position}`,
    className
  ].filter(Boolean).join(' ');
  
  const popoverContent = (
    <div
      ref={popoverRef as React.Ref<HTMLDivElement>}
      className={popoverClasses}
      style={{
        position: 'fixed',
        top: popoverPosition.top,
        left: popoverPosition.left,
        maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
        visibility: isPositioned ? 'visible' : 'hidden',
        opacity: isPositioned ? 1 : 0,
        ...style
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="core-popover__content">
        {/* Subtitle section */}
        {subtitle && (
          <div className="core-popover__subtitle">
            {subtitle}
          </div>
        )}
        
        {/* Main content section */}
        <div className="core-popover__main">
          {content}
        </div>
        
        {/* State section */}
        {state && (
          <div className={`core-popover__state core-popover__state--${stateType}`}>
            {state}
          </div>
        )}
      </div>
      
      {/* Arrow/caret - only show if requested */}
      {showArrow && <div className="core-popover__arrow" />}
    </div>
  );
  
  // Render in a portal to avoid z-index issues
  return createPortal(popoverContent, document.body);
});

Popover.displayName = 'Popover';

export default Popover;
